import * as React from 'react';
import {RouteComponentProps, withRouter} from 'react-router';
import {ColumnProps} from 'antd/lib/table';
import {Button, Switch, Table} from 'antd';
import {Link} from 'react-router-dom';
import * as tableUtils from "../utils/table-utils";
import {GridState, searchableSortableColumn, sortableColumn} from "../utils/table-utils";
import locales from '../locales';
import {ErrorAlert} from '../common/ErrorAlert';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import furoApi from '../api/FuroApi';
import {ErrorResponse, GetInvoicesRequest, InvoicesSummary, PageResponseEnvelope} from '../api/dtos/dtos';
import {renderUnitColumn} from '../utils/number-utils';
import * as paths from '../paths';
import * as routerUtils from '../utils/router-utils';
import {YearMonth, YearMonthRangePicker} from "../statistics/utils/YearMonthRangePicker";
import SectionHeaderFormItem from "../common/form-items/SectionHeaderFormItem";
import moment from "moment";
import ButtonBar from "../common/ButtonBar";
import {InvoiceSource} from "./invoice";

interface State extends GridState<InvoicesSummary> {
  error?: ErrorResponse;
  start?: YearMonth;
  end?: YearMonth;
  showDeletedOnly: boolean;
}

class InvoicesPage extends React.Component<RouteComponentProps, State> {

  public state: State = {
    // Default initial grid state
    ...tableUtils.initialGridState(),

    // Initial sorting
    sortColumnKey: 'clientName',
    sortOrder: 'ascend',

    start: {year: moment().get('year'), month: moment().get('month')},
    end: {year: moment().get('year'), month: moment().get('month')},
    showDeletedOnly: false,

    // Override with cached state from Router
    ...this.props.location.state,
  };

  public readonly loadingHandler?: LoadingOverlayHandler;

  public componentDidMount() {
    this.reload();
  }

  public render() {
    const {error, start, end, showDeletedOnly} = this.state;
    const columns: Array<ColumnProps<InvoicesSummary>> = [
      searchableSortableColumn(locales.client, 'clientName', this.state, this.renderClient),
      sortableColumn(locales.total, 'totalInvoiceCount', this.state, this.renderTotalCount),
      sortableColumn(locales.open_invoice, 'createdInvoiceCount', this.state,this.renderCreatedCount),
    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <SectionHeaderFormItem title={locales.range}/>
        <YearMonthRangePicker
          start={start}
          end={end}
          onRangeClear={this.onRangeClear}
          onRangePick={this.onRangePick}
        />

        {start && end && (
          <>
            <ButtonBar style={{marginBottom: 8}}>
              <span>{locales.show_only_deleted}</span>
              <Switch style={{marginRight: 16, marginLeft: 4}} checked={showDeletedOnly}
                      onChange={this.onShowDeletedOnlyChange}/>

              <Button size="small" type="primary" icon="plus"
                      onClick={this.onAddInvoiceClick}>{locales.add}</Button>
            </ButtonBar>

            <Table
              dataSource={this.state.data}
              columns={columns}
              rowKey={this.getRowKey}
              bordered={true}
              size="small"
              pagination={tableUtils.defaultPaginationConfig(this.state)}
              onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
              scroll={{x: 500}}
            />
          </>
        )}

        {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
      </>
    );
  }

  private getRowKey = (record: InvoicesSummary): string => {
    return tableUtils.idRowKeyFn(record.client)
  }

  private renderTotalCount = (record: InvoicesSummary): React.ReactNode => {
    return renderUnitColumn(record.totalInvoiceCount, locales.pcs);
  };

  private renderCreatedCount = (record: InvoicesSummary): React.ReactNode => {
    return renderUnitColumn(record.createdInvoiceCount, locales.pcs);
  };

  private renderClient = (record: InvoicesSummary): React.ReactNode => {
    const clientId = record.client?.id;
    const clientName = record.client?.name;
    return clientId && clientName
      ? <Link
        to={{
          pathname: paths.clientInvoicesPath(clientId),
          state: {
            start: this.state.start,
            end: this.state.end,
            showDeletedOnly: this.state.showDeletedOnly,
          },
        }}
      >
        {clientName}
      </Link>
      : '-';
  };

  private onGridChange = (newState: GridState<InvoicesSummary>) => {
    this.setState({...newState}, () => this.reload());
  };

  private onShowDeletedOnlyChange = (checked: boolean) => {
    this.setState({showDeletedOnly: checked, page: 1}, () => this.reload());
  };

  private onRangeClear = () => {
    this.setState({
      start: undefined,
      end: undefined,
    });
  };

  private onRangePick = (start: YearMonth | undefined, end: YearMonth | undefined) => {
    this.setState({start, end}, () => {
      if (start && end) {
        this.reload();
      }
    });
  };

  private onAddInvoiceClick = () => {
    this.props.history.push(paths.createInvoicePath, {
      year: this.state.start?.year,
      month: this.state.start?.month,
      source: InvoiceSource.MANUAL,
    });
  }


  private reload = () => {
    const {start, end} = this.state;

    const request: GetInvoicesRequest = {
      grid: tableUtils.toGridRequest(this.state),
      deleted: this.state.showDeletedOnly,
      startYear: start?.year!,
      startMonth: start?.month!,
      endYear: end?.year!,
      endMonth: end?.month!,
    };

    const promise = furoApi.invoicing.getInvoices(request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onReloadSuccess = (response: PageResponseEnvelope<InvoicesSummary>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    }, () => {
      routerUtils.cacheComponentStateForLocation(this.props, this.state);
    });
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };
}

export default withRouter(InvoicesPage);



// WEBPACK FOOTER //
// ./src/invoicing/InvoicesPage.tsx